import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { UserPlus, Users } from "lucide-react";

/**
 * Reusable customer picker that lets the operator:
 *   1) choose an existing customer, OR
 *   2) create a brand new "cari kart" inline from the same screen.
 *
 * `onChange` is called with { id, name, phone, email, address, tax_no } so
 * the parent form can keep flat fields in sync with its own payload.
 *
 * Props:
 *   value            : current customer_id
 *   onChange(info)   : called whenever the selection changes OR a new one is created
 *   label            : optional field label (default "Müşteri")
 *   testid           : optional data-testid prefix (default "customer-picker")
 *   allowManualName  : if true, show a free-text "veya elle ad" fallback
 *   manualName       : controlled free-text name (optional)
 *   onManualName     : handler for free-text name changes (optional)
 */
export default function CustomerPicker({
  value,
  onChange,
  label = "Müşteri",
  testid = "customer-picker",
  allowManualName = false,
  manualName,
  onManualName,
}) {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", tax_no: "", address: "", notes: "" });

  const reload = () => api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const pick = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) onChange({ id: c.id, name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", tax_no: c.tax_no || "" });
  };

  const createCustomer = async () => {
    if (!form.name.trim()) return toast.error("Müşteri adı zorunludur");
    setBusy(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Cari kart oluşturuldu");
      setCustomers((cs) => [data, ...cs]);
      onChange({ id: data.id, name: data.name, phone: data.phone || "", email: data.email || "", address: data.address || "", tax_no: data.tax_no || "" });
      setOpen(false);
      setForm({ name: "", phone: "", email: "", tax_no: "", address: "", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5"><Users size={13} className="text-slate-500" /> {label}</Label>
      <div className="flex gap-2">
        <div className="flex-1">
          <Select value={value || ""} onValueChange={pick}>
            <SelectTrigger data-testid={`${testid}-select`}><SelectValue placeholder="Mevcut cari seçin" /></SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{c.name}</span>
                    {c.phone && <span className="text-xs text-slate-500 font-mono">· {c.phone}</span>}
                  </div>
                </SelectItem>
              ))}
              {customers.length === 0 && <div className="px-2 py-3 text-xs text-slate-400">Henüz cari yok. Sağdaki + butonundan ekleyin.</div>}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 gap-1 border-[#1B4D3E] text-[#1B4D3E] hover:bg-emerald-50"
          onClick={() => setOpen(true)}
          data-testid={`${testid}-new`}
        >
          <UserPlus size={14} /> Yeni
        </Button>
      </div>
      {allowManualName && (
        <Input
          className="mt-1"
          placeholder="veya elle ad (cari oluşturmadan)"
          value={manualName || ""}
          onChange={(e) => onManualName?.(e.target.value)}
          data-testid={`${testid}-manual`}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} className="text-[#1B4D3E]" /> Yeni Cari Kart
            </DialogTitle>
            <DialogDescription>Siparişi bu müşteriye bağlamak için hızlıca cari kartını oluşturun.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Ad Soyad / Firma Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${testid}-name`} autoFocus />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`${testid}-phone`} placeholder="05xx xxx xx xx" />
            </div>
            <div>
              <Label>E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testid}-email`} />
            </div>
            <div>
              <Label>TC / Vergi No</Label>
              <Input value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} data-testid={`${testid}-tax-no`} />
            </div>
            <div className="sm:col-span-2">
              <Label>Adres</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid={`${testid}-address`} />
            </div>
            <div className="sm:col-span-2">
              <Label>Not</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`${testid}-notes`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button
              className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2"
              onClick={createCustomer}
              disabled={busy}
              data-testid={`${testid}-submit`}
            >
              <UserPlus size={14} /> {busy ? "Oluşturuluyor…" : "Cari Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
